import React from "react";
import Reveal from "../common/Reveal";

type CardLayout = {
  title: string;
  description: string;
  badge: string;
  accentGradient: string;
  cta?: string;
};

const HIGHLIGHT_CARDS: CardLayout[] = [
  {
    title: "Thoughtful Learning Paths",
    description:
      "Forget rigid drills. We’ve structured our lessons to feel like a natural journey, turning daily practice into the best part of your day.",
    badge: "Your Journey",
    accentGradient: "from-amber-50 via-pink-50 to-white",
  },
  {
    title: "Friendly Expert Feedback",
    description:
      "Stuck on a chord? Our coaches provide gentle, professional guidance in live sessions to help you fix habits before they stick.",
    badge: "Mentorship",
    accentGradient: "from-sky-50 via-cyan-50 to-white",
  },
  {
    title: "Milestone Celebrations",
    description:
      "Your progress deserves an audience. We help you polish your best performances and give you a supportive stage to share them.",
    badge: "Spotlight",
    accentGradient: "from-stone-50 via-zinc-50 to-white",
  }
];
const Highlights: React.FC = () => {
  return (
    <section id="brands" className="relative bg-primary/95 py-20 sm:py-28">
      <div className="mx-auto flex max-w-6xl flex-col gap-12 px-6 lg:gap-16">
        <Reveal className="max-w-2xl space-y-4 text-center lg:text-left">
          <p className="text-xs uppercase tracking-[0.35em] text-accentNavy/50">
            Experience Highlights
          </p>
          <h2 className="font-display text-3xl text-accentNavy sm:text-4xl lg:text-5xl">
            Crafted journeys that make practice feel like performance.
          </h2>
          <p className="text-base text-accentNavy/70 lg:text-lg">
            Each pathway blends mentorship and tactile exploration so
            you stay inspired long after the first chord. Discover how students
            turn curiosity into confident stage presence.
          </p>
        </Reveal>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {HIGHLIGHT_CARDS.map(
            ({ title, description, badge, accentGradient, cta }, index) => (
              <Reveal key={title} delay={index * 80}>
                <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-accentNavy/10 bg-white/90 p-8 shadow-[0_4px_20px_-8px_rgba(9,32,63,0.15)] transition-all duration-500 hover:-translate-y-1 hover:border-accentNavy/20 hover:shadow-[0_8px_30px_-12px_rgba(9,32,63,0.25)]">
                  {/* Subtle gradient accent on hover */}
                  <div
                    className={[
                      "pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100",
                      accentGradient,
                    ].join(" ")}
                  />
                  
                  {/* Content */}
                  <div className="relative z-10 flex h-full flex-col">
                    <div className="mb-6">
                      <span className="inline-block rounded-lg border border-accentNavy/10 bg-white/80 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-accentNavy/70 transition-colors duration-300 group-hover:border-accentNavy/20 group-hover:bg-white group-hover:text-accentNavy/90">
                        {badge}
                      </span>
                    </div>
                    
                    <div className="flex-1 space-y-4">
                      <h3 className="text-2xl font-semibold leading-tight text-accentNavy">
                        {title}
                      </h3>
                      <p className="text-sm leading-relaxed text-accentNavy/70 lg:text-base">
                        {description}
                      </p>
                    </div>
                    
                    <div className="mt-8 flex items-center justify-between border-t border-accentNavy/5 pt-6">
                      <span className="text-sm font-semibold uppercase tracking-[0.15em] text-accentNavy/60 transition-colors duration-300 group-hover:text-accentNavy/80">
                        {cta ?? "Discover more"}
                      </span>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full border border-accentNavy/10 bg-white transition-all duration-300 group-hover:border-accentNavy/20 group-hover:translate-x-1 group-hover:bg-accentNavy/5">
                        <svg
                          className="h-4 w-4 text-accentNavy/60 transition-colors duration-300 group-hover:text-accentNavy"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Animated border glow on hover */}
                  <div className="pointer-events-none absolute inset-0 rounded-2xl border-2 border-accentNavy/0 transition-all duration-500 group-hover:border-accentNavy/10" />
                </article>
              </Reveal>
            )
          )}
        </div>
      </div>
    </section>
  );
};

export default Highlights;